import { useMemo } from 'react';
import { Alert, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { EvacZone, RegionEvacZoneStyle } from 'shared/types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import {
  CreateGeoEventProvisionalSubmissionResponse,
  EditGeoEventProvisionalSubmissionResponse,
} from 'features/provisionalSubmissions/provisionalSubmissions.types';
import { getDateFormatted } from 'shared/dates';
import { convertProvisionalSubmissionDataToFormValues } from '../../hooks/useInitialValues';
import { WildfireChangelog } from '../WildfireChangelog';

type ReviewEditWildfireChangesProps = {
  wildfire: Wildfire;
  reviewSubmission:
    | CreateGeoEventProvisionalSubmissionResponse
    | EditGeoEventProvisionalSubmissionResponse;
  evacZones: EvacZone[];
  zoneStyle: RegionEvacZoneStyle;
};

export const ReviewEditWildfireChanges = ({
  wildfire,
  reviewSubmission,
  evacZones,
  zoneStyle,
}: ReviewEditWildfireChangesProps): JSX.Element => {
  const { t } = useTranslation();

  const submittedFormValues = useMemo(() => {
    const { submittedData } = reviewSubmission;
    if (submittedData.geoEventType !== 'wildfire') return undefined;
    return convertProvisionalSubmissionDataToFormValues(
      submittedData,
      reviewSubmission.media,
      wildfire,
    );
  }, [reviewSubmission, wildfire]);
  const methods = useForm({ defaultValues: submittedFormValues });

  return (
    <Stack spacing={1.5}>
      <Alert severity="info">
        {t('reviewSubmission.submittedByEditGeoEvent', {
          firstName: reviewSubmission.createdBy.firstName,
          lastName: reviewSubmission.createdBy.lastName,
          date: getDateFormatted(reviewSubmission.dateCreated, {
            includeYear: true,
            includeTimeZone: true,
          }),
        })}
      </Alert>

      <FormProvider {...methods}>
        <WildfireChangelog
          wildfire={wildfire}
          evacZones={evacZones}
          zoneStyle={zoneStyle}
          title={t('reviewSubmission.geoEventChanges.title')}
          hideConfirmations
        />
      </FormProvider>
    </Stack>
  );
};
